import React, { useContext, useState } from 'react'
import { Button, makeStyles, Text, tokens } from '@fluentui/react-components'
import { CheckmarkCircle20Filled, Mail20Regular } from '@fluentui/react-icons'
import { t } from '../../i18n/i18n'
import { DataContext } from '../../App'
import { OrganisationWithUserAccountsDTO, UserAccount } from '../../api/schemas/schema'
import { Modal } from '../../components/common/modal/Modal'
import { sendWelcomeEmail } from '../../helpers/emailSender'
import { IWelcomeEmailStatus, wasCreatedBeforeTracking } from '../../helpers/welcomeEmailHelper'
import { formatDate } from '../../helpers/dateHelper'

type WelcomeEmailPanelProps = {
   organisation: OrganisationWithUserAccountsDTO
   welcomeEmailStatus: IWelcomeEmailStatus
   onWelcomeEmailSent: () => void
   /** Osparade ändringar får inte mailas ut — mottagarna skulle kunna vara adresser som inte finns i systemet. */
   hasUnsavedChanges: boolean
}

const useStyles = makeStyles({
   panel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacingHorizontalXL,
      margin: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXXL} 0`,
      padding: tokens.spacingHorizontalL,
      backgroundColor: tokens.colorNeutralBackground2,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      borderRadius: tokens.borderRadiusMedium,
      '@media (max-width: 1007px)': {
         flexDirection: 'column',
         alignItems: 'stretch',
      },
   },
   details: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXXS,
      minWidth: 0,
   },
   status: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalXS,
      color: tokens.colorNeutralForeground2,
   },
   sent: {
      color: tokens.colorPaletteGreenForeground1,
      flexShrink: 0,
   },
   recipients: {
      color: tokens.colorNeutralForeground3,
      overflowWrap: 'anywhere',
   },
   hint: {
      color: tokens.colorNeutralForeground3,
   },
   action: {
      flexShrink: 0,
   },
   recipientList: {
      margin: `${tokens.spacingVerticalS} 0 0`,
      paddingLeft: tokens.spacingHorizontalXXL,
   },
})

export const WelcomeEmailPanel = ({ organisation, welcomeEmailStatus, onWelcomeEmailSent, hasUnsavedChanges }: WelcomeEmailPanelProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const [showConfirmation, setShowConfirmation] = useState(false)

   const userAccounts: UserAccount[] = organisation?.userAccounts ?? []
   const receipt = welcomeEmailStatus.byOrganisationId.get(organisation?.organisation?.id)
   const sentBeforeTracking = wasCreatedBeforeTracking(organisation?.organisation, welcomeEmailStatus)
   const hasNoRecipients = userAccounts.length === 0

   const isDisabled = hasUnsavedChanges || hasNoRecipients

   const hint = hasUnsavedChanges ? t('SaveBeforeSendingWelcomeEmail') : hasNoRecipients ? t('NoRecipientsForWelcomeEmail') : null

   const send = () => {
      setShowConfirmation(false)
      sendWelcomeEmail(dataContext, organisation.organisation.id, userAccounts, onWelcomeEmailSent)
   }

   return (
      <>
         <div className={classes.panel}>
            <div className={classes.details}>
               <Text weight="semibold">{t('WelcomeEmail')}</Text>

               {receipt ? (
                  <>
                     <span className={classes.status}>
                        <CheckmarkCircle20Filled className={classes.sent} />
                        <Text size={200}>{`${t('WelcomeEmailSentAt')} ${formatDate(receipt.sentAt)}`}</Text>
                     </span>
                     {receipt.sentTo && (
                        <Text size={200} className={classes.recipients}>{`${t('MailWasSentTo')} ${receipt.sentTo}`}</Text>
                     )}
                  </>
               ) : (
                  <Text size={200} className={classes.status}>
                     {sentBeforeTracking ? t('WelcomeEmailSentPreviously') : t('WelcomeEmailNotSent')}
                  </Text>
               )}

               {hint && (
                  <Text size={200} className={classes.hint}>
                     {hint}
                  </Text>
               )}
            </div>

            <Button
               className={classes.action}
               appearance="secondary"
               icon={<Mail20Regular />}
               disabled={isDisabled}
               onClick={() => setShowConfirmation(true)}
            >
               {receipt ? t('SendWelcomeEmailAgain') : t('SendWelcomeEmail')}
            </Button>
         </div>

         <Modal
            open={showConfirmation}
            title={receipt ? t('SendWelcomeEmailAgain') : t('SendWelcomeEmail')}
            children={
               <div>
                  {receipt && <p>{`${t('WelcomeEmailAlreadySent')} ${formatDate(receipt.sentAt)}.`}</p>}
                  <p>{t('SendWelcomeEmailConfirmation')}</p>
                  <ul className={classes.recipientList}>
                     {userAccounts.map((userAccount) => (
                        <li key={userAccount.id}>{`${userAccount.firstName} ${userAccount.lastName} – ${userAccount.email}`}</li>
                     ))}
                  </ul>
               </div>
            }
            actions={
               <>
                  <Button appearance="subtle" onClick={() => setShowConfirmation(false)}>
                     {t('Cancel')}
                  </Button>
                  <Button appearance="primary" onClick={send}>
                     {t('Send')}
                  </Button>
               </>
            }
         />
      </>
   )
}
